/**
 * ClientTextImportDialog
 *
 * Diálogo para importar un texto del cliente (email, WhatsApp, nota) a un
 * evento EXISTENTE. Extrae con IA imperdibles, vetos, playlist Spotify,
 * momentos especiales con timing, notas técnicas, tareas y briefing.
 *
 * Flujo:
 *   1) Input: textarea (opcionalmente pre-rellenado)
 *   2) Análisis: spinner + llamada a /api/dj-assistant/extract-client-text
 *   3) Preview: revisión ÍTEM POR ÍTEM (canciones, momentos, notas, tareas,
 *      horarios, gustos) → checkbox individual + edición inline
 *   4) Apply: llamada a /api/dj-assistant/apply-client-text-extraction
 *   5) Done: toast + onApplied()
 *
 * Reutilización: si se pasa `externalExtraction`, el diálogo salta
 * directamente a la fase 'preview' con esa extracción ya calculada (usado
 * por WhatsAppImporter, que hace su propio análisis de chat/audios y solo
 * necesita la revisión + aplicación, no la extracción de texto libre).
 *
 * Fecha: Mayo 2026 (iter92.B) — Ampliado Jun 2026: revisión por ítem de
 * momentos/notas/tareas/horarios (antes solo canciones) + modo reutilizable.
 */

import { useState, useEffect } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import {
  Sparkles,
  Loader2,
  X,
  Music,
  Ban,
  ExternalLink,
  Clock,
  Wrench,
  ListTodo,
  Bot,
  Mic,
  AlertCircle,
  Search,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API_URL } from '../config';
import { runAiJob } from '../utils/aiJobs';

const API = API_URL;
let _itemSeq = 0;
const nextItemId = (prefix) => `${prefix}-${Date.now()}-${_itemSeq++}`;

const HORARIO_KEYS = [
  ['cocktail', 'Cóctel'],
  ['ceremonia', 'Ceremonia'],
  ['banquete', 'Banquete'],
  ['fiesta_baile', 'Inicio fiesta/baile'],
  ['fin_fiesta', 'Fin de fiesta'],
];

const SECTION_LABELS = {
  imperdibles: 'Canciones imperdibles',
  prohibidas: 'Canciones prohibidas (vetos)',
  gustos: 'Gustos musicales',
  playlist_spotify: 'Playlist Spotify del cliente',
  momentos: 'Momentos especiales',
  horarios: 'Horarios',
  notas_tecnicas: 'Notas técnicas',
  tareas: 'Tareas pendientes',
  briefing: 'Briefing IA',
};

const ClientTextImportDialog = ({
  open,
  onClose,
  eventId,
  eventName,
  initialText = '',
  onApplied = null,
  externalExtraction = null,
  title = 'Importar texto del cliente',
  helpText = 'Pega el email, WhatsApp o nota del cliente. La IA extraerá imperdibles, vetos, momentos especiales, playlist Spotify, horarios, notas y tareas. Después podrás revisar elemento por elemento antes de aplicar.',
  source = 'email',
}) => {
  const [phase, setPhase] = useState('input'); // input | analyzing | preview | applying
  const [text, setText] = useState('');
  const [extraction, setExtraction] = useState(null);
  const [selected, setSelected] = useState({});
  // Candidatas con revisión a nivel de ítem (igual que en GestionDocumentos):
  // checkbox individual, edición inline y, para canciones, re-búsqueda Spotify
  // y destino "informativo" vs "petición con momento" (petición real que el
  // DJ ve en su lista de trabajo del directo).
  const [songCandidates, setSongCandidates] = useState([]);
  const [momentoItems, setMomentoItems] = useState([]);
  const [notaItems, setNotaItems] = useState([]);
  const [tareaItems, setTareaItems] = useState([]);
  const [horarioItems, setHorarioItems] = useState([]);
  const [gustoItems, setGustoItems] = useState([]);
  const [searchingId, setSearchingId] = useState(null);
  const [spotifyOptions, setSpotifyOptions] = useState({});

  // Construye TODAS las listas de ítems revisables a partir de una extracción
  // (llamada tras /extract-client-text o directamente con externalExtraction).
  const buildAllItemsFromExtraction = (exData) => {
    const candidates = [];
    // Títulos ya cubiertos por una canción de un momento especial, para no
    // duplicar la misma canción también como "imperdible" suelta (el LLM a
    // veces la lista en ambos sitios) — así, si se desmarca el momento padre,
    // la canción desaparece del todo en vez de quedar colada por otra vía.
    const momentoSongTitles = new Set();
    (exData.momentos_especiales || []).forEach((m) => {
      (m.canciones || []).forEach((c) => {
        if (c.titulo) momentoSongTitles.add(c.titulo.toLowerCase().trim());
      });
    });
    (exData.canciones_imperdibles || []).forEach((c) => {
      if (!c.titulo || momentoSongTitles.has(c.titulo.toLowerCase().trim())) return;
      candidates.push({
        id: nextItemId('song'), origen: 'imperdible', origenLabel: c.contexto || '',
        titulo: c.titulo, artista: c.artista || '', selected: true,
        destino: 'informativo', momento: '', spotify_url: '', spotify_uri: '',
      });
    });
    (exData.canciones_prohibidas || []).forEach((c) => {
      if (!c.titulo) return;
      candidates.push({
        id: nextItemId('song'), origen: 'prohibida', origenLabel: c.motivo || '',
        titulo: c.titulo, artista: c.artista || '', selected: true,
        destino: 'informativo', momento: '', spotify_url: '', spotify_uri: '',
      });
    });
    (exData.momentos_especiales || []).forEach((m, momentoIdx) => {
      (m.canciones || []).forEach((c, cancionIdx) => {
        if (!c.titulo) return;
        candidates.push({
          id: nextItemId('song'), origen: 'momento', origenLabel: `${m.titulo || m.tipo}${c.rol ? ` (${c.rol})` : ''}`,
          momentoIdx, cancionIdx,
          titulo: c.titulo, artista: c.artista || '', selected: true,
          destino: 'peticion', momento: m.titulo || m.tipo, spotify_url: '', spotify_uri: '',
        });
      });
    });
    setSongCandidates(candidates);

    setMomentoItems((exData.momentos_especiales || []).map((m, idx) => ({
      id: nextItemId('momento'), idx, selected: true,
      tipo: m.tipo || 'otro', titulo: m.titulo || '', descripcion: m.descripcion || '',
      cuando: m.cuando || '', participantes: m.participantes || '', frase_dj: m.frase_dj || '',
    })));
    setNotaItems((exData.notas_tecnicas || []).map((n) => ({
      id: nextItemId('nota'), selected: true, texto: n || '',
    })));
    setTareaItems((exData.tareas_pendientes || []).map((t) => ({
      id: nextItemId('tarea'), selected: true,
      descripcion: t.descripcion || '', asignar_a: t.asignar_a || '', deadline: t.deadline || '',
    })));
    setHorarioItems(
      HORARIO_KEYS.filter(([k]) => exData.horarios && exData.horarios[k]).map(([k, label]) => ({
        id: nextItemId('horario'), key: k, selected: true, label, value: exData.horarios[k],
      }))
    );
    setGustoItems((exData.gustos_musicales || []).map((g) => ({
      id: nextItemId('gusto'), selected: true, texto: g || '',
    })));

    const defaultSel = {};
    if ((exData.canciones_imperdibles || []).length) defaultSel.imperdibles = true;
    if ((exData.canciones_prohibidas || []).length) defaultSel.prohibidas = true;
    if ((exData.gustos_musicales || []).length) defaultSel.gustos = true;
    if (exData.playlist_spotify_url) defaultSel.playlist_spotify = true;
    if ((exData.momentos_especiales || []).length) defaultSel.momentos = true;
    if (exData.horarios && Object.values(exData.horarios).some(Boolean)) defaultSel.horarios = true;
    if ((exData.notas_tecnicas || []).length) defaultSel.notas_tecnicas = true;
    if ((exData.tareas_pendientes || []).length) defaultSel.tareas = true;
    if (exData.briefing_ia) defaultSel.briefing = true;
    setSelected(defaultSel);
  };

  useEffect(() => {
    if (open) {
      setSpotifyOptions({});
      if (externalExtraction) {
        setText('');
        setExtraction(externalExtraction);
        buildAllItemsFromExtraction(externalExtraction);
        setPhase('preview');
      } else {
        setPhase('input');
        setText(initialText || '');
        setExtraction(null);
        setSongCandidates([]);
        setMomentoItems([]);
        setNotaItems([]);
        setTareaItems([]);
        setHorarioItems([]);
        setGustoItems([]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialText, externalExtraction]);

  if (!open) return null;

  const analyze = async () => {
    if (!text.trim()) {
      toast.error('Pega o escribe un texto');
      return;
    }
    setPhase('analyzing');
    try {
      const token = localStorage.getItem('token');
      const { data } = await runAiJob({
        startUrl: `${API}/dj-assistant/extract-client-text/start`,
        statusBase: `${API}/dj-assistant/jobs`,
        body: { text: text.trim(), event_id: eventId },
        config: { headers: { Authorization: `Bearer ${token}` } },
      });
      setExtraction(data.extraction);
      buildAllItemsFromExtraction(data.extraction || {});
      setPhase('preview');
    } catch (e) {
      console.error('extract error', e);
      toast.error(e.response?.data?.detail || 'Error analizando el texto');
      setPhase('input');
    }
  };

  const toggleSongSelected = (id) => {
    setSongCandidates((prev) => prev.map((c) => (c.id === id ? { ...c, selected: !c.selected } : c)));
  };
  const updateSongField = (id, field, value) => {
    setSongCandidates((prev) => prev.map((c) => (c.id === id ? { ...c, [field]: value } : c)));
  };
  const toggleSongDestino = (id) => {
    setSongCandidates((prev) => prev.map((c) => (
      c.id === id ? { ...c, destino: c.destino === 'peticion' ? 'informativo' : 'peticion' } : c
    )));
  };
  const reSearchSpotify = async (candidate) => {
    const titleQ = (candidate.titulo || '').trim();
    const artist = (candidate.artista || '').trim();
    if (!titleQ) {
      toast.warning('Escribe un título primero');
      return;
    }
    const query = `${titleQ} ${artist}`.trim();
    setSearchingId(candidate.id);
    try {
      const res = await fetch(`${API}/spotify/public/search?query=${encodeURIComponent(query)}`, { method: 'GET' });
      let data = {};
      try {
        data = await res.json();
      } catch (parseErr) {
        toast.error(res.ok ? `Respuesta no válida: ${parseErr?.message}` : `Error Spotify (${res.status})`);
        return;
      }
      if (!res.ok) {
        toast.error(data.detail || `Error en Spotify search (${res.status})`);
        return;
      }
      const tracks = (data.tracks || []).slice(0, 5);
      if (tracks.length === 0) {
        toast.info('Spotify no encontró match para esa búsqueda');
        setSpotifyOptions((prev) => ({ ...prev, [candidate.id]: [] }));
        return;
      }
      setSpotifyOptions((prev) => ({ ...prev, [candidate.id]: tracks }));
    } catch (e) {
      toast.error(`Error: ${e.message || e}`);
    } finally {
      setSearchingId(null);
    }
  };
  const applySpotifyOption = (candidateId, track) => {
    setSongCandidates((prev) => prev.map((c) => (
      c.id === candidateId
        ? {
            ...c,
            titulo: track.name || c.titulo,
            artista: (track.artists && track.artists[0] && track.artists[0].name) || c.artista,
            spotify_url: track.spotify_url || '',
            spotify_uri: track.uri || '',
          }
        : c
    )));
    setSpotifyOptions((prev) => {
      const next = { ...prev };
      delete next[candidateId];
      return next;
    });
  };
  const closeSpotifyOptions = (candidateId) => {
    setSpotifyOptions((prev) => {
      const next = { ...prev };
      delete next[candidateId];
      return next;
    });
  };

  // Toggle/edit genéricos para momentos, notas, tareas y horarios.
  const toggleMomentoSelected = (id) => setMomentoItems((prev) => prev.map((m) => (m.id === id ? { ...m, selected: !m.selected } : m)));
  const updateMomentoField = (id, field, value) => setMomentoItems((prev) => prev.map((m) => (m.id === id ? { ...m, [field]: value } : m)));
  const toggleNotaSelected = (id) => setNotaItems((prev) => prev.map((n) => (n.id === id ? { ...n, selected: !n.selected } : n)));
  const updateNotaField = (id, field, value) => setNotaItems((prev) => prev.map((n) => (n.id === id ? { ...n, [field]: value } : n)));
  const toggleTareaSelected = (id) => setTareaItems((prev) => prev.map((t) => (t.id === id ? { ...t, selected: !t.selected } : t)));
  const updateTareaField = (id, field, value) => setTareaItems((prev) => prev.map((t) => (t.id === id ? { ...t, [field]: value } : t)));
  const toggleHorarioSelected = (id) => setHorarioItems((prev) => prev.map((h) => (h.id === id ? { ...h, selected: !h.selected } : h)));
  const updateHorarioField = (id, field, value) => setHorarioItems((prev) => prev.map((h) => (h.id === id ? { ...h, [field]: value } : h)));
  const toggleGustoSelected = (id) => setGustoItems((prev) => prev.map((g) => (g.id === id ? { ...g, selected: !g.selected } : g)));

  // Una canción solo cuenta/aplica si su checkbox individual Y el checkbox del
  // bloque al que pertenece están activos. Para canciones de un momento,
  // además el momento concreto (por índice estable) debe seguir seleccionado.
  const songBlockEnabled = (candidate) => {
    if (candidate.origen === 'imperdible') return !!selected.imperdibles;
    if (candidate.origen === 'prohibida') return !!selected.prohibidas;
    if (!selected.momentos) return false;
    const parent = momentoItems.find((m) => m.idx === candidate.momentoIdx);
    return !!parent?.selected;
  };

  const apply = async () => {
    const fields = Object.keys(selected).filter((k) => selected[k]);
    const checkedSongs = songCandidates.filter((c) => c.selected && songBlockEnabled(c));
    // Las "prohibidas" nunca se convierten en petición (serían un veto contradictorio);
    // solo imperdibles y canciones de momentos especiales pueden ir a "petición con momento".
    const peticionSongs = checkedSongs.filter((c) => c.destino === 'peticion' && c.origen !== 'prohibida');
    const checkedMomentos = selected.momentos ? momentoItems.filter((m) => m.selected) : [];
    const checkedNotas = selected.notas_tecnicas ? notaItems.filter((n) => n.selected) : [];
    const checkedTareas = selected.tareas ? tareaItems.filter((t) => t.selected) : [];
    const checkedHorarios = selected.horarios ? horarioItems.filter((h) => h.selected) : [];
    const checkedGustos = selected.gustos ? gustoItems.filter((g) => g.selected) : [];
    const hasWork = checkedSongs.length > 0 || checkedMomentos.length > 0 || checkedNotas.length > 0
      || checkedTareas.length > 0 || checkedHorarios.length > 0 || checkedGustos.length > 0
      || (selected.playlist_spotify && ex.playlist_spotify_url) || (selected.briefing && ex.briefing_ia);
    if (fields.length === 0 || !hasWork) {
      toast.error('Selecciona al menos un elemento para aplicar');
      return;
    }
    setPhase('applying');
    try {
      const token = localStorage.getItem('token');
      const authHeader = { headers: { Authorization: `Bearer ${token}` } };

      // 1) Canciones marcadas como "petición con momento" → mismo endpoint que usa
      //    el flujo de Documentos, para que el DJ las vea como peticiones reales
      //    en su lista de trabajo del directo, con momento y match de Spotify.
      let peticionesCreadas = 0;
      let peticionesOmitidas = 0;
      if (peticionSongs.length > 0) {
        const { data: petData } = await axios.post(
          `${API}/playlists/${eventId}/peticiones-batch-add`,
          {
            peticiones: peticionSongs.map((c) => ({
              titulo: c.titulo,
              artista: c.artista || '',
              momento: c.momento || '',
              spotify_url: c.spotify_url || undefined,
              spotify_uri: c.spotify_uri || undefined,
              tipo: c.spotify_url ? 'spotify_link' : 'manual',
              source: 'client_text_review',
            })),
          },
          { ...authHeader, timeout: 30000 }
        );
        peticionesCreadas = petData.created ?? 0;
        peticionesOmitidas = petData.skipped ?? 0;
      }

      // 2) Resto de bloques: se reconstruye la extracción SOLO con los ítems que
      //    siguen marcados (individual Y de bloque) y con las ediciones del
      //    usuario ya aplicadas, para no duplicar lo que ya se creó como
      //    petición real arriba.
      const informativeImperdibles = checkedSongs.filter((c) => c.origen === 'imperdible' && c.destino === 'informativo');
      const informativeProhibidas = checkedSongs.filter((c) => c.origen === 'prohibida');
      const editedExtraction = {
        ...ex,
        canciones_imperdibles: informativeImperdibles.map((c) => ({ titulo: c.titulo, artista: c.artista, contexto: c.origenLabel })),
        canciones_prohibidas: informativeProhibidas.map((c) => ({ titulo: c.titulo, artista: c.artista, motivo: c.origenLabel })),
        momentos_especiales: checkedMomentos.map((m) => ({
          tipo: m.tipo,
          titulo: m.titulo,
          descripcion: m.descripcion,
          cuando: m.cuando,
          participantes: m.participantes,
          frase_dj: m.frase_dj,
          // Canciones de este momento que siguen marcadas pero NO se convirtieron
          // en petición real (se quedan como nota dentro del momento estructurado).
          canciones: songCandidates
            .filter((sc) => sc.origen === 'momento' && sc.momentoIdx === m.idx && sc.selected && sc.destino !== 'peticion')
            .map((sc) => ({ titulo: sc.titulo, artista: sc.artista, rol: 'principal' })),
        })),
        notas_tecnicas: checkedNotas.map((n) => n.texto).filter(Boolean),
        tareas_pendientes: checkedTareas
          .map((t) => ({ descripcion: t.descripcion, asignar_a: t.asignar_a || null, deadline: t.deadline || null }))
          .filter((t) => t.descripcion),
        horarios: Object.fromEntries(checkedHorarios.map((h) => [h.key, h.value])),
        gustos_musicales: checkedGustos.map((g) => g.texto).filter(Boolean),
      };

      const { data } = await axios.post(
        `${API}/dj-assistant/apply-client-text-extraction`,
        { event_id: eventId, extraction: editedExtraction, fields_selected: fields, source },
        { ...authHeader, timeout: 30000 }
      );
      const s = data.summary || {};
      const pl = (n, singular, plural) => `${n} ${n === 1 ? singular : (plural || `${singular}s`)}`;
      const parts = [];
      if (peticionesCreadas) parts.push(`${pl(peticionesCreadas, 'petición', 'peticiones')} con momento`);
      if (peticionesOmitidas) parts.push(`${peticionesOmitidas} ya ${peticionesOmitidas === 1 ? 'existía' : 'existían'}`);
      if (s.imperdibles_added) parts.push(pl(s.imperdibles_added, 'imperdible'));
      if (s.prohibidas_added) parts.push(pl(s.prohibidas_added, 'veto'));
      if (s.gustos_added) parts.push(pl(s.gustos_added, 'gusto'));
      if (s.momentos_added) parts.push(pl(s.momentos_added, 'momento'));
      if (s.notas_tecnicas_added) parts.push(pl(s.notas_tecnicas_added, 'nota'));
      if (s.tareas_created) parts.push(pl(s.tareas_created, 'tarea'));
      if (s.playlist_spotify_added) parts.push('playlist Spotify');
      if (s.briefing_saved) parts.push('briefing');
      toast.success(`Aplicado: ${parts.join(', ') || 'cambios guardados'}`);
      if (onApplied) onApplied(data);
      onClose();
    } catch (e) {
      console.error('apply error', e);
      toast.error(e.response?.data?.detail || 'Error aplicando los cambios');
      setPhase('preview');
    }
  };

  const toggle = (k) => setSelected((prev) => ({ ...prev, [k]: !prev[k] }));

  const ex = extraction || {};

  return (
    <DialogPrimitive.Root open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogPrimitive.Portal>
        <div
          className="fixed inset-0 z-[110] flex items-center justify-center p-3 bg-black/75 backdrop-blur-sm"
          onClick={onClose}
          data-testid="client-text-import-modal"
        >
          <DialogPrimitive.Content
            onClick={(e) => e.stopPropagation()}
            className="bg-slate-900 border border-emerald-500/40 rounded-xl shadow-2xl max-w-2xl w-full max-h-[92vh] flex flex-col outline-none"
          >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-700">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-emerald-400" />
            <div>
              <DialogPrimitive.Title asChild>
                <h3 className="text-base font-semibold text-white">{title}</h3>
              </DialogPrimitive.Title>
              <DialogPrimitive.Description className="sr-only">
                Revisión con IA del texto, canciones y notas del cliente antes de aplicar al evento
              </DialogPrimitive.Description>
              {eventName && (
                <p className="text-xs text-emerald-300/80">→ {eventName}</p>
              )}
            </div>
          </div>
          <Button
            onClick={onClose}
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 text-slate-400 hover:text-white"
            data-testid="client-text-import-close"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Body */}
        <ScrollArea className="flex-1">
          <div className="p-4 space-y-4">
            {phase === 'input' && (
              <>
                <p className="text-xs text-slate-400">{helpText}</p>
                <Textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Pega aquí el texto del cliente..."
                  className="min-h-[260px] bg-slate-800 border-slate-700 text-slate-200 placeholder:text-slate-600 text-sm"
                  data-testid="client-text-import-textarea"
                />
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={onClose} className="border-slate-700 text-slate-400">
                    Cancelar
                  </Button>
                  <Button
                    onClick={analyze}
                    disabled={!text.trim()}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="client-text-import-analyze-btn"
                  >
                    <Sparkles className="h-4 w-4 mr-2" />
                    Analizar con IA
                  </Button>
                </div>
              </>
            )}

            {phase === 'analyzing' && (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <Loader2 className="h-10 w-10 text-emerald-400 animate-spin" />
                <p className="text-sm text-slate-300">Analizando texto con GPT-5.2...</p>
                <p className="text-xs text-slate-500">Esto puede tardar 10-30 segundos</p>
              </div>
            )}

            {phase === 'preview' && (
              <div className="space-y-3" data-testid="client-text-import-preview">
                <p className="text-xs text-slate-400">
                  Revisa cada ítem y desmarca/edita lo que no quieras aplicar tal cual:
                </p>

                {/* Imperdibles */}
                {songCandidates.some((c) => c.origen === 'imperdible') && (
                  <PreviewBlock
                    icon={<Music className="h-4 w-4 text-emerald-400" />}
                    label={SECTION_LABELS.imperdibles}
                    checked={!!selected.imperdibles}
                    onToggle={() => toggle('imperdibles')}
                    testId="block-imperdibles"
                  >
                    <div className="space-y-1">
                      {songCandidates.filter((c) => c.origen === 'imperdible').map((c) => (
                        <SongCandidateRow
                          key={c.id}
                          candidate={c}
                          onToggleSelected={toggleSongSelected}
                          onUpdateField={updateSongField}
                          onToggleDestino={toggleSongDestino}
                          onReSearch={reSearchSpotify}
                          searching={searchingId === c.id}
                          spotifyOpts={spotifyOptions[c.id]}
                          onApplyOption={applySpotifyOption}
                          onCloseOptions={closeSpotifyOptions}
                        />
                      ))}
                    </div>
                  </PreviewBlock>
                )}

                {/* Prohibidas */}
                {songCandidates.some((c) => c.origen === 'prohibida') && (
                  <PreviewBlock
                    icon={<Ban className="h-4 w-4 text-red-400" />}
                    label={SECTION_LABELS.prohibidas}
                    checked={!!selected.prohibidas}
                    onToggle={() => toggle('prohibidas')}
                    testId="block-prohibidas"
                  >
                    <div className="space-y-1">
                      {songCandidates.filter((c) => c.origen === 'prohibida').map((c) => (
                        <SongCandidateRow
                          key={c.id}
                          candidate={c}
                          onToggleSelected={toggleSongSelected}
                          onUpdateField={updateSongField}
                          lockDestinoInformativo
                        />
                      ))}
                    </div>
                  </PreviewBlock>
                )}

                {/* Gustos */}
                {gustoItems.length > 0 && (
                  <PreviewBlock
                    icon={<Music className="h-4 w-4 text-purple-400" />}
                    label={SECTION_LABELS.gustos}
                    checked={!!selected.gustos}
                    onToggle={() => toggle('gustos')}
                    testId="block-gustos"
                  >
                    <div className="flex flex-wrap gap-1">
                      {gustoItems.map((item) => (
                        <GustoChip key={item.id} item={item} onToggle={toggleGustoSelected} />
                      ))}
                    </div>
                    <p className="text-[10px] text-slate-500 mt-1.5">Pulsa un género para incluirlo/excluirlo.</p>
                  </PreviewBlock>
                )}

                {/* Playlist Spotify */}
                {ex.playlist_spotify_url && (
                  <PreviewBlock
                    icon={<ExternalLink className="h-4 w-4 text-green-400" />}
                    label={SECTION_LABELS.playlist_spotify}
                    checked={!!selected.playlist_spotify}
                    onToggle={() => toggle('playlist_spotify')}
                    testId="block-playlist"
                  >
                    <a
                      href={ex.playlist_spotify_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-green-300 underline break-all"
                    >
                      {ex.playlist_spotify_url}
                    </a>
                    <p className="text-[10px] text-slate-500 mt-1">
                      Se añadirá como playlist cliente — el wizard IA la usará al generar la lista personalizada.
                    </p>
                  </PreviewBlock>
                )}

                {/* Momentos especiales — revisión individual: checkbox, título,
                    descripción, cuándo/participantes y frase del DJ editables.
                    Las canciones vinculadas a cada momento se gestionan dentro,
                    con el mismo control que imperdibles/vetos. */}
                {momentoItems.length > 0 && (
                  <PreviewBlock
                    icon={<Mic className="h-4 w-4 text-amber-400" />}
                    label={SECTION_LABELS.momentos}
                    checked={!!selected.momentos}
                    onToggle={() => toggle('momentos')}
                    testId="block-momentos"
                  >
                    <div className="space-y-2">
                      {momentoItems.map((item) => (
                        <MomentoItemRow key={item.id} item={item} onToggle={toggleMomentoSelected} onUpdate={updateMomentoField}>
                          {songCandidates.some((c) => c.origen === 'momento' && c.momentoIdx === item.idx) && (
                            <div className="mt-1.5 space-y-1 pl-1 border-l-2 border-amber-600/30">
                              {songCandidates
                                .filter((c) => c.origen === 'momento' && c.momentoIdx === item.idx)
                                .map((c) => (
                                  <SongCandidateRow
                                    key={c.id}
                                    candidate={c}
                                    onToggleSelected={toggleSongSelected}
                                    onUpdateField={updateSongField}
                                    onToggleDestino={toggleSongDestino}
                                    onReSearch={reSearchSpotify}
                                    searching={searchingId === c.id}
                                    spotifyOpts={spotifyOptions[c.id]}
                                    onApplyOption={applySpotifyOption}
                                    onCloseOptions={closeSpotifyOptions}
                                    compact
                                  />
                                ))}
                            </div>
                          )}
                        </MomentoItemRow>
                      ))}
                    </div>
                    <p className="text-[10px] text-amber-400/80 mt-2">
                      Las canciones de un momento se crean por defecto como <b>petición real con ese momento</b> —
                      desmárcalas o pulsa ↔ para dejarlas solo como nota. Cada momento (título, descripción,
                      cuándo y frase del DJ) es editable y queda guardado como momento estructurado del evento.
                    </p>
                  </PreviewBlock>
                )}

                {/* Horarios — ítem por ítem, cada hora con su propio checkbox */}
                {horarioItems.length > 0 && (
                  <PreviewBlock
                    icon={<Clock className="h-4 w-4 text-blue-400" />}
                    label={SECTION_LABELS.horarios}
                    checked={!!selected.horarios}
                    onToggle={() => toggle('horarios')}
                    testId="block-horarios"
                  >
                    <div className="space-y-1">
                      {horarioItems.map((item) => (
                        <HorarioItemRow key={item.id} item={item} onToggle={toggleHorarioSelected} onUpdate={updateHorarioField} />
                      ))}
                    </div>
                  </PreviewBlock>
                )}

                {/* Notas técnicas — ítem por ítem, texto editable */}
                {notaItems.length > 0 && (
                  <PreviewBlock
                    icon={<Wrench className="h-4 w-4 text-cyan-400" />}
                    label={SECTION_LABELS.notas_tecnicas}
                    checked={!!selected.notas_tecnicas}
                    onToggle={() => toggle('notas_tecnicas')}
                    testId="block-notas-tecnicas"
                  >
                    <div className="space-y-1">
                      {notaItems.map((item) => (
                        <NotaItemRow key={item.id} item={item} onToggle={toggleNotaSelected} onUpdate={updateNotaField} />
                      ))}
                    </div>
                  </PreviewBlock>
                )}

                {/* Tareas — ítem por ítem, descripción y asignación editables */}
                {tareaItems.length > 0 && (
                  <PreviewBlock
                    icon={<ListTodo className="h-4 w-4 text-orange-400" />}
                    label={SECTION_LABELS.tareas}
                    checked={!!selected.tareas}
                    onToggle={() => toggle('tareas')}
                    testId="block-tareas"
                  >
                    <div className="space-y-1.5">
                      {tareaItems.map((item) => (
                        <TareaItemRow key={item.id} item={item} onToggle={toggleTareaSelected} onUpdate={updateTareaField} />
                      ))}
                    </div>
                  </PreviewBlock>
                )}

                {/* Briefing */}
                {ex.briefing_ia && (
                  <PreviewBlock
                    icon={<Bot className="h-4 w-4 text-pink-400" />}
                    label={SECTION_LABELS.briefing}
                    checked={!!selected.briefing}
                    onToggle={() => toggle('briefing')}
                    testId="block-briefing"
                  >
                    {ex.tono_cliente && (
                      <Badge className="bg-pink-700 text-[10px] mb-2">Tono: {ex.tono_cliente}</Badge>
                    )}
                    <p className="text-xs text-slate-300 whitespace-pre-wrap">{ex.briefing_ia}</p>
                  </PreviewBlock>
                )}

                {songCandidates.length === 0 && momentoItems.length === 0 && notaItems.length === 0
                  && tareaItems.length === 0 && horarioItems.length === 0 && gustoItems.length === 0
                  && !ex.playlist_spotify_url && !ex.briefing_ia && (
                  <div className="flex items-center gap-2 p-3 bg-amber-900/30 border border-amber-700/50 rounded">
                    <AlertCircle className="h-4 w-4 text-amber-400" />
                    <span className="text-xs text-amber-300">No se extrajo información útil del texto</span>
                  </div>
                )}
              </div>
            )}

            {phase === 'applying' && (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <Loader2 className="h-10 w-10 text-emerald-400 animate-spin" />
                <p className="text-sm text-slate-300">Aplicando cambios al evento...</p>
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Footer (solo en preview) */}
        {phase === 'preview' && (
          <div className="flex items-center justify-between gap-2 p-3 border-t border-slate-700 bg-slate-900/80">
            <span className="text-[11px] text-slate-400 leading-snug">
              {(() => {
                const pl = (n, singular, plural) => `${n} ${n === 1 ? singular : (plural || `${singular}s`)}`;
                const nSongs = songCandidates.filter((c) => c.selected && songBlockEnabled(c)).length;
                const nPeticiones = songCandidates.filter((c) => c.selected && songBlockEnabled(c) && c.destino === 'peticion' && c.origen !== 'prohibida').length;
                const nMomentos = selected.momentos ? momentoItems.filter((m) => m.selected).length : 0;
                const nNotas = selected.notas_tecnicas ? notaItems.filter((n) => n.selected).length : 0;
                const nTareas = selected.tareas ? tareaItems.filter((t) => t.selected).length : 0;
                const nHorarios = selected.horarios ? horarioItems.filter((h) => h.selected).length : 0;
                return `${pl(nSongs, 'canción', 'canciones')} (${pl(nPeticiones, 'petición', 'peticiones')}) · ${pl(nMomentos, 'momento')} · ${pl(nNotas, 'nota')} · ${pl(nTareas, 'tarea')} · ${pl(nHorarios, 'horario')}`;
              })()}
            </span>
            <div className="flex gap-2 shrink-0">
              <Button
                variant="outline"
                onClick={() => (externalExtraction ? onClose() : setPhase('input'))}
                className="border-slate-700 text-slate-400"
                data-testid="client-text-import-back-btn"
              >
                {externalExtraction ? 'Cancelar' : '← Volver al texto'}
              </Button>
              <Button
                onClick={apply}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="client-text-import-apply-btn"
              >
                Aplicar al evento
              </Button>
            </div>
          </div>
        )}
          </DialogPrimitive.Content>
        </div>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};

const PreviewBlock = ({ icon, label, checked, onToggle, children, testId }) => (
  <div
    className={`border rounded-lg p-3 transition-colors ${
      checked ? 'border-emerald-500/40 bg-slate-800/60' : 'border-slate-700 bg-slate-800/30 opacity-60'
    }`}
    data-testid={testId}
  >
    <label className="flex items-start gap-2 cursor-pointer mb-2">
      <Checkbox checked={checked} onCheckedChange={onToggle} data-testid={`${testId}-checkbox`} />
      <div className="flex items-center gap-1.5 text-sm font-medium text-white">
        {icon}
        {label}
      </div>
    </label>
    <div className="pl-6">{children}</div>
  </div>
);

// Fila de revisión de una canción candidata (imperdible / prohibida / de un momento especial):
// checkbox individual, edición inline de título/artista, re-búsqueda en Spotify con top-5,
// y toggle de destino "informativo" (CSV de imperdibles/vetos) vs "petición con momento"
// (crea una petición real que el DJ ve en su lista de trabajo del directo). Mismo patrón
// que usa GestionDocumentos.jsx para mantener la app coherente entre los flujos de IA.
const SongCandidateRow = ({
  candidate: c,
  onToggleSelected,
  onUpdateField,
  onToggleDestino,
  onReSearch,
  searching,
  spotifyOpts,
  onApplyOption,
  onCloseOptions,
  lockDestinoInformativo = false,
  compact = false,
}) => (
  <div
    className={`flex items-start gap-2 p-1.5 rounded border transition-colors ${
      c.selected ? 'bg-slate-900/50 border-slate-700/60' : 'bg-slate-900/20 border-slate-800 opacity-50'
    }`}
    data-testid={`client-text-song-${c.id}`}
  >
    <Checkbox
      checked={c.selected}
      onCheckedChange={() => onToggleSelected(c.id)}
      className="mt-1.5"
      data-testid={`client-text-song-checkbox-${c.id}`}
    />
    <div className="flex-1 min-w-0 space-y-1">
      <div className="flex items-center gap-1.5 flex-wrap">
        <input
          type="text"
          value={c.titulo || ''}
          onChange={(e) => onUpdateField(c.id, 'titulo', e.target.value)}
          placeholder="Título"
          className={`flex-1 min-w-0 bg-transparent text-white font-medium ${compact ? 'text-xs' : 'text-sm'} border-b border-transparent hover:border-slate-600 focus:border-emerald-500 focus:outline-none px-0.5`}
          data-testid={`client-text-song-titulo-${c.id}`}
        />
        {!lockDestinoInformativo && onReSearch && (
          <button
            type="button"
            onClick={() => onReSearch(c)}
            disabled={searching || !(c.titulo || '').trim()}
            className="flex-shrink-0 p-1 rounded text-slate-400 hover:text-emerald-300 hover:bg-emerald-900/20 disabled:opacity-40"
            title="Re-buscar en Spotify"
            data-testid={`client-text-song-research-${c.id}`}
          >
            {searching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
          </button>
        )}
        {!lockDestinoInformativo && onToggleDestino && (
          <button
            type="button"
            onClick={() => onToggleDestino(c.id)}
            className="flex-shrink-0 p-1 rounded text-slate-400 hover:text-amber-300 hover:bg-amber-900/20"
            title={c.destino === 'peticion' ? 'Dejar solo como nota informativa' : 'Convertir en petición real con momento'}
            data-testid={`client-text-song-swap-${c.id}`}
          >
            <span className="text-sm leading-none" aria-hidden="true">↔</span>
          </button>
        )}
      </div>
      <div className="flex items-center gap-1.5 pl-0.5">
        <span className="text-slate-500 text-[10px] uppercase tracking-wide w-12 flex-shrink-0">Artista</span>
        <input
          type="text"
          value={c.artista || ''}
          onChange={(e) => onUpdateField(c.id, 'artista', e.target.value)}
          placeholder="Artista (opcional)"
          className="flex-1 min-w-0 bg-transparent text-slate-300 text-xs border-b border-transparent hover:border-slate-600 focus:border-emerald-500 focus:outline-none px-0.5"
          data-testid={`client-text-song-artista-${c.id}`}
        />
      </div>
      {!lockDestinoInformativo && (
        c.destino === 'peticion' ? (
          <div className="flex items-center gap-1.5 pl-0.5">
            <span className="text-amber-300/70 text-[10px] uppercase tracking-wide w-12 flex-shrink-0">📍 Mom.</span>
            <input
              type="text"
              value={c.momento || ''}
              onChange={(e) => onUpdateField(c.id, 'momento', e.target.value)}
              placeholder="Momento (ej. Corte tarta)"
              className="flex-1 min-w-0 bg-transparent text-amber-300 text-[11px] border-b border-transparent hover:border-slate-600 focus:border-amber-500 focus:outline-none px-0.5"
              data-testid={`client-text-song-momento-${c.id}`}
            />
            <span className="text-[9px] px-1 py-0.5 bg-amber-500/20 text-amber-300 rounded flex-shrink-0">PETICIÓN</span>
          </div>
        ) : (
          <span className="text-[9px] px-1 py-0.5 bg-slate-700/50 text-slate-400 rounded">INFORMATIVO</span>
        )
      )}
      {c.origenLabel && (
        <p className="text-slate-500 text-[10px] italic pl-0.5">↳ {c.origenLabel}</p>
      )}
      {spotifyOpts && spotifyOpts.length > 0 && (
        <div className="mt-1.5 ml-0.5 bg-slate-950/80 border border-emerald-500/40 rounded p-2 space-y-1">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] text-emerald-300 uppercase tracking-wide font-bold">🎯 Elige el match correcto:</span>
            <button type="button" onClick={() => onCloseOptions(c.id)} className="text-slate-500 hover:text-slate-300 p-0.5">
              <X className="w-3 h-3" />
            </button>
          </div>
          {spotifyOpts.map((track, idx) => {
            const isCurrent = c.spotify_url === track.spotify_url;
            const cover = track.album?.images?.[track.album.images.length - 1]?.url || track.album?.images?.[0]?.url;
            const artistsName = (track.artists || []).map((a) => a.name).join(', ');
            return (
              <button
                key={track.id || idx}
                type="button"
                onClick={() => onApplyOption(c.id, track)}
                className={`w-full flex items-center gap-2 p-1.5 rounded text-left transition-colors ${
                  isCurrent ? 'bg-emerald-900/30 border border-emerald-500/50' : 'bg-slate-900/60 border border-slate-700 hover:border-emerald-500/40 hover:bg-emerald-900/10'
                }`}
                data-testid={`client-text-song-spotify-option-${c.id}-${idx}`}
              >
                {cover ? (
                  <img src={cover} alt="" className="w-8 h-8 rounded flex-shrink-0 object-cover" />
                ) : (
                  <div className="w-8 h-8 rounded bg-slate-800 flex-shrink-0 flex items-center justify-center">
                    <Music className="w-4 h-4 text-slate-600" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-white font-medium truncate">{track.name}</div>
                  <div className="text-[10px] text-slate-400 truncate">
                    {artistsName} · <span className="text-slate-500">{track.album?.name || ''}</span>
                  </div>
                </div>
                {isCurrent && <span className="text-[9px] px-1 py-0.5 bg-emerald-500/30 text-emerald-300 rounded flex-shrink-0">ACTUAL</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  </div>
);

// Fila de revisión de un momento especial (ej. "bingo musical después del
// cóctel", "no hay baile nupcial"): checkbox + título/descripción/cuándo/
// participantes/frase del DJ editables. Al aplicar, se guarda como momento
// ESTRUCTURADO del evento (con id propio), visible en la planificación —
// no como texto suelto.
const MomentoItemRow = ({ item, onToggle, onUpdate, children }) => (
  <div
    className={`rounded border p-2 space-y-1.5 transition-colors ${
      item.selected ? 'bg-slate-900/50 border-amber-700/40' : 'bg-slate-900/20 border-slate-800 opacity-50'
    }`}
    data-testid={`client-text-momento-${item.id}`}
  >
    <div className="flex items-start gap-2">
      <Checkbox
        checked={item.selected}
        onCheckedChange={() => onToggle(item.id)}
        className="mt-1"
        data-testid={`client-text-momento-checkbox-${item.id}`}
      />
      <div className="flex-1 min-w-0 space-y-1">
        <div className="flex items-center gap-1.5 flex-wrap">
          <Badge className="bg-amber-700 text-[10px] shrink-0">{item.tipo}</Badge>
          <input
            type="text"
            value={item.titulo}
            onChange={(e) => onUpdate(item.id, 'titulo', e.target.value)}
            placeholder="Título del momento"
            className="flex-1 min-w-0 bg-transparent text-white text-sm font-medium border-b border-transparent hover:border-slate-600 focus:border-amber-500 focus:outline-none px-0.5"
            data-testid={`client-text-momento-titulo-${item.id}`}
          />
        </div>
        <textarea
          value={item.descripcion}
          onChange={(e) => onUpdate(item.id, 'descripcion', e.target.value)}
          placeholder="Descripción / detalles..."
          rows={2}
          className="w-full bg-slate-950/40 text-slate-300 text-xs rounded p-1.5 border border-transparent hover:border-slate-700 focus:border-amber-500 focus:outline-none resize-none"
          data-testid={`client-text-momento-descripcion-${item.id}`}
        />
        <div className="flex items-center gap-2 flex-wrap text-[10px]">
          <input
            type="text"
            value={item.cuando}
            onChange={(e) => onUpdate(item.id, 'cuando', e.target.value)}
            placeholder="¿Cuándo?"
            className="w-32 bg-transparent text-slate-400 border-b border-transparent hover:border-slate-600 focus:border-amber-500 focus:outline-none px-0.5"
            data-testid={`client-text-momento-cuando-${item.id}`}
          />
          <input
            type="text"
            value={item.participantes}
            onChange={(e) => onUpdate(item.id, 'participantes', e.target.value)}
            placeholder="Participantes"
            className="flex-1 min-w-0 bg-transparent text-slate-400 border-b border-transparent hover:border-slate-600 focus:border-amber-500 focus:outline-none px-0.5"
            data-testid={`client-text-momento-participantes-${item.id}`}
          />
        </div>
        <input
          type="text"
          value={item.frase_dj}
          onChange={(e) => onUpdate(item.id, 'frase_dj', e.target.value)}
          placeholder="Frase que debe decir el DJ (opcional)"
          className="w-full bg-transparent text-emerald-300 italic text-[11px] border-b border-transparent hover:border-slate-600 focus:border-amber-500 focus:outline-none px-0.5"
          data-testid={`client-text-momento-frase-${item.id}`}
        />
        {children}
      </div>
    </div>
  </div>
);

// Fila de revisión de una nota técnica individual (ej. "saxofonista en el
// cóctel", "no tienen baile nupcial"): checkbox + texto editable.
const NotaItemRow = ({ item, onToggle, onUpdate }) => (
  <div
    className={`flex items-start gap-2 p-1.5 rounded border ${
      item.selected ? 'bg-slate-900/50 border-slate-700/60' : 'bg-slate-900/20 border-slate-800 opacity-50'
    }`}
    data-testid={`client-text-nota-${item.id}`}
  >
    <Checkbox
      checked={item.selected}
      onCheckedChange={() => onToggle(item.id)}
      className="mt-1"
      data-testid={`client-text-nota-checkbox-${item.id}`}
    />
    <input
      type="text"
      value={item.texto}
      onChange={(e) => onUpdate(item.id, 'texto', e.target.value)}
      className="flex-1 min-w-0 bg-transparent text-slate-300 text-xs border-b border-transparent hover:border-slate-600 focus:border-cyan-500 focus:outline-none px-0.5"
      data-testid={`client-text-nota-texto-${item.id}`}
    />
  </div>
);

// Fila de revisión de una tarea pendiente individual: checkbox + descripción
// y a quién se asigna, ambos editables.
const TareaItemRow = ({ item, onToggle, onUpdate }) => (
  <div
    className={`p-1.5 rounded border space-y-1 ${
      item.selected ? 'bg-slate-900/50 border-slate-700/60' : 'bg-slate-900/20 border-slate-800 opacity-50'
    }`}
    data-testid={`client-text-tarea-${item.id}`}
  >
    <div className="flex items-start gap-2">
      <Checkbox
        checked={item.selected}
        onCheckedChange={() => onToggle(item.id)}
        className="mt-1"
        data-testid={`client-text-tarea-checkbox-${item.id}`}
      />
      <input
        type="text"
        value={item.descripcion}
        onChange={(e) => onUpdate(item.id, 'descripcion', e.target.value)}
        className="flex-1 min-w-0 bg-transparent text-slate-200 text-xs border-b border-transparent hover:border-slate-600 focus:border-orange-500 focus:outline-none px-0.5"
        data-testid={`client-text-tarea-descripcion-${item.id}`}
      />
    </div>
    <div className="flex items-center gap-2 pl-6">
      <input
        type="text"
        value={item.asignar_a}
        onChange={(e) => onUpdate(item.id, 'asignar_a', e.target.value)}
        placeholder="Asignar a (cliente/dj/venue)"
        className="flex-1 min-w-0 bg-transparent text-orange-300 text-[10px] border-b border-transparent hover:border-slate-600 focus:border-orange-500 focus:outline-none px-0.5"
        data-testid={`client-text-tarea-asignar-${item.id}`}
      />
    </div>
  </div>
);

// Fila de revisión de un horario individual (ej. "Cóctel: 13:00"): checkbox
// + valor editable.
const HorarioItemRow = ({ item, onToggle, onUpdate }) => (
  <div
    className={`flex items-center gap-2 p-1.5 rounded border ${
      item.selected ? 'bg-slate-900/50 border-slate-700/60' : 'bg-slate-900/20 border-slate-800 opacity-50'
    }`}
    data-testid={`client-text-horario-${item.id}`}
  >
    <Checkbox
      checked={item.selected}
      onCheckedChange={() => onToggle(item.id)}
      data-testid={`client-text-horario-checkbox-${item.id}`}
    />
    <span className="text-slate-500 text-[10px] w-28 shrink-0">{item.label}</span>
    <input
      type="text"
      value={item.value}
      onChange={(e) => onUpdate(item.id, 'value', e.target.value)}
      className="flex-1 min-w-0 bg-transparent text-slate-200 text-xs border-b border-transparent hover:border-slate-600 focus:border-blue-500 focus:outline-none px-0.5"
      data-testid={`client-text-horario-value-${item.id}`}
    />
  </div>
);

// Chip de género musical que se puede incluir/excluir con un click.
const GustoChip = ({ item, onToggle }) => (
  <button
    type="button"
    onClick={() => onToggle(item.id)}
    className={`text-[10px] px-2 py-0.5 rounded-full border transition-colors ${
      item.selected ? 'bg-purple-700 border-purple-600 text-white' : 'bg-transparent border-slate-700 text-slate-500 line-through'
    }`}
    data-testid={`client-text-gusto-${item.id}`}
  >
    {item.texto}
  </button>
);

export default ClientTextImportDialog;
